/**
 * Création du compte d'un enfant par son parent (migration 0301) — sans email :
 * l'enfant utilisera l'app sur le téléphone du parent. Mêmes informations qu'à
 * l'onboarding élève (prénom, date de naissance, classe, matières) ; le parent
 * donne son accord parental en créant le compte.
 */

import { useState } from 'react';
import { Button } from '@jeraisonne/ui';
import { SECONDARY_SCHOOL_LEVELS, SUBJECTS, type SchoolLevel } from '@jeraisonne/db';
import { useCreateManagedChild } from '@/lib/parent/managed-children';

const inputCls =
  'border-ink-200 text-ink-900 focus:ring-primary-400 h-11 w-full rounded-xl border bg-white px-3 text-sm focus:outline-none focus:ring-2';

export function CreateChildForm({
  parentId,
  onCreated,
}: {
  parentId: string;
  onCreated: (child: { child_id: string; display_name: string }) => void;
}) {
  const create = useCreateManagedChild(parentId);
  const [name, setName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [level, setLevel] = useState<SchoolLevel | ''>('');
  const [subjects, setSubjects] = useState<string[]>([]);
  const available = SUBJECTS.filter((s) => !level || s.levels.includes(level));
  const canSubmit = name.trim() !== '' && birthdate !== '' && level !== '' && !create.isPending;

  return (
    <form
      className="border-ink-100 flex flex-col gap-4 rounded-2xl border bg-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        create.mutate(
          {
            display_name: name.trim(),
            birthdate,
            level,
            subjects: subjects.filter((s) => available.some((a) => a.value === s)),
          },
          { onSuccess: onCreated },
        );
      }}
    >
      <div>
        <p className="font-display text-ink-900 text-base font-bold">
          Créer le compte de mon enfant
        </p>
        <p className="text-ink-500 mt-1 text-sm">
          Pas besoin d’email : ton enfant révisera sur ton téléphone. Tu passeras sur son compte en
          un geste, et tu reviendras sur le tien avec ton code parent.
        </p>
      </div>
      <label className="flex flex-col gap-1">
        <span className="text-ink-700 text-sm font-semibold">Prénom</span>
        <input
          className={inputCls}
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-ink-700 text-sm font-semibold">Date de naissance</span>
        <input
          type="date"
          className={inputCls}
          value={birthdate}
          onChange={(e) => setBirthdate(e.target.value)}
        />
      </label>
      <fieldset className="flex flex-col gap-2">
        <legend className="text-ink-700 mb-1 text-sm font-semibold">Classe</legend>
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-7">
          {SECONDARY_SCHOOL_LEVELS.map((l) => (
            <button
              key={l.value}
              type="button"
              aria-pressed={level === l.value}
              onClick={() => setLevel(l.value)}
              className={
                level === l.value
                  ? 'bg-primary-600 min-h-[2.75rem] rounded-xl text-sm font-semibold text-white'
                  : 'border-ink-200 text-ink-700 min-h-[2.75rem] rounded-xl border bg-white text-sm font-semibold active:scale-[0.98]'
              }
            >
              {l.label}
            </button>
          ))}
        </div>
      </fieldset>
      {level !== '' && available.length > 0 && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-ink-700 mb-1 text-sm font-semibold">Matières</legend>
          <div className="flex flex-wrap gap-2">
            {available.map((s) => {
              const on = subjects.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  aria-pressed={on}
                  onClick={() =>
                    setSubjects((cur) =>
                      on ? cur.filter((v) => v !== s.value) : [...cur, s.value],
                    )
                  }
                  className={
                    on
                      ? 'bg-primary-50 border-primary-400 text-primary-700 min-h-[2.75rem] rounded-full border px-4 text-sm font-semibold'
                      : 'border-ink-200 text-ink-700 min-h-[2.75rem] rounded-full border bg-white px-4 text-sm font-semibold'
                  }
                >
                  {s.emoji} {s.label}
                </button>
              );
            })}
          </div>
        </fieldset>
      )}
      {create.error && (
        <p className="text-sm text-red-600" role="alert">
          {create.error.message}
        </p>
      )}
      <p className="text-ink-500 text-xs">
        En créant ce compte, tu donnes ton accord parental pour que ton enfant utilise JeRaisonne
        (RGPD).
      </p>
      <Button type="submit" fullWidth disabled={!canSubmit}>
        {create.isPending ? 'Création…' : 'Créer son compte'}
      </Button>
    </form>
  );
}
